// Todas las rutas del API que usa la web, en un solo módulo (facilita alinear con apps/api).
import type { AcuseLinea, LoginInput } from '@tarja/contracts';
import type { Marca } from '@tarja/ui';
import { HttpClient, type Query } from './client';
import type {
  Aprobacion,
  Auditoria,
  Carga,
  CatalogoRoles,
  CodigoEnrolamiento,
  ConflictoPersona,
  Control,
  Cuarentena,
  Dispositivo,
  EstadoRegistro,
  Evento,
  Integracion,
  Jornada,
  JornadaDetalle,
  LogIntegracion,
  LoteNomina,
  Onboarding,
  Pagina,
  Parametro,
  PermisoAsignacion,
  Periodo,
  Reclamo,
  RegistroInput,
  Reparto,
  RespuestaInvitacion,
  RespuestaLogin,
  RespuestaPermisos,
  RespuestaRegistro,
  Resultado,
  ResultadoSimulacion,
  Tarifario,
  TokensSesion,
  Usuario,
  UsuarioAdmin,
  EnlaceAcceso,
  OrganizacionDetalle,
  OrganizacionResumen,
  RegistroAuditoriaPlataforma,
  RespuestaOrganizacion,
  ResumenPlataforma,
  UsuarioPlataforma,
} from './types';

/** Respuesta de GET /auth/me en apps/api (tenants como ids, sin nombre ni tipo de usuario). */
export interface UsuarioCrudo {
  usuario_id: string;
  tenant_id: string | null;
  admin_plataforma?: boolean;
  tenants: (string | { tenant_id: string; nombre?: string })[];
  rol_base: Usuario['rol_base'];
  permisos?: { sociedad_id: string; permiso: string }[];
  sociedades?: string[];
  persona_id?: string | null;
  nombre?: string;
  login?: string;
  tipo_usuario?: Usuario['tipo_usuario'];
  sociedad_id?: string | null;
  amr?: string[];
}

export function normalizarUsuario(u: UsuarioCrudo): Usuario {
  const permisos = u.permisos ?? [];
  const esContratista = u.tipo_usuario === 'externo' || permisos.some((p) => p.permiso === 'aprobador_contratista');
  const r: Usuario = {
    usuario_id: u.usuario_id,
    login: u.login ?? u.usuario_id,
    nombre: u.nombre ?? u.login ?? 'Usuario',
    rol_base: u.rol_base,
    tenant_id: u.tenant_id,
    tenants: (u.tenants ?? (u.tenant_id ? [u.tenant_id] : [])).map((t) => (typeof t === 'string' ? { tenant_id: t, nombre: t } : { tenant_id: t.tenant_id, nombre: t.nombre ?? t.tenant_id })),
    permisos,
    sociedad_id: u.sociedad_id ?? (esContratista ? (permisos.find((p) => p.permiso === 'aprobador_contratista')?.sociedad_id ?? u.sociedades?.[0] ?? null) : null),
  };
  if (u.tipo_usuario) r.tipo_usuario = u.tipo_usuario;
  if (u.admin_plataforma) r.admin_plataforma = true;
  return r;
}

export const API_BASE: string = import.meta.env.VITE_API_BASE || '/api/tarja/v1';

export const http = new HttpClient({ baseUrl: API_BASE });

/** Acepta `{items,next_cursor}` o un arreglo simple. */
export function normalizarPagina<T>(r: Pagina<T> | T[] | undefined | null): Pagina<T> {
  if (!r) return { items: [], next_cursor: null };
  if (Array.isArray(r)) return { items: r, next_cursor: null };
  return { items: r.items ?? [], next_cursor: r.next_cursor ?? null };
}

async function pagina<T>(path: string, query?: Query): Promise<Pagina<T>> {
  return normalizarPagina(await http.get<Pagina<T> | T[]>(path, query));
}

/** Recursos de maestros con CRUD estándar (§7.2). */
export type RecursoMaestro =
  | 'sociedades'
  | 'personas'
  | 'vinculos'
  | 'trabajadores-campo'
  | 'jerarquia/nodos'
  | 'permisos'
  | 'ubicaciones'
  | 'labores'
  | 'unidades'
  | 'conversiones'
  | 'conceptos'
  | 'tarifarios'
  | 'tarifas'
  | 'asignaciones-tarifario'
  | 'excepciones-tarifa'
  | 'metas'
  | 'bonos';

export const api = {
  auth: {
    login: (datos: LoginInput) => http.post<RespuestaLogin>('/auth/login', datos, { sinReintento: true }),
    verificarMfa: (mfa_token: string, codigo: string) =>
      http.post<{ access_token: string; expires_in: number }>('/auth/mfa/verify', { mfa_token, codigo }, { sinReintento: true }),
    logout: () => http.post<void>('/auth/logout', undefined, { sinReintento: true }),
    me: async () => normalizarUsuario(await http.get<UsuarioCrudo>('/auth/me')),
    pedirReset: (login: string) => http.post<void>('/auth/password/reset-request', { login }, { sinReintento: true }),
    restablecer: (token: string, password: string) => http.post<void>('/auth/password/reset', { token, password }, { sinReintento: true }),
    estadoRegistro: () => http.get<EstadoRegistro>('/auth/registro', undefined, { sinReintento: true }),
    registrar: (datos: RegistroInput) => http.post<RespuestaRegistro>('/auth/registro', datos, { sinReintento: true }),
    aceptarInvitacion: (datos: { token: string; nombre?: string; password: string }) => http.post<TokensSesion>('/auth/invitacion', datos, { sinReintento: true }),
  },

  cuenta: {
    cambiarPassword: (actual: string, nueva: string) => http.post<void>('/cuenta/password', { actual, nueva }),
    iniciarMfa: () => http.post<{ secreto: string; otpauth_url: string; token: string }>('/cuenta/mfa/iniciar', {}),
    confirmarMfa: (token: string, codigo: string) => http.post<void>('/cuenta/mfa/confirmar', { token, codigo }),
  },

  roles: {
    catalogo: () => http.get<CatalogoRoles>('/roles/catalogo'),
  },

  admin: {
    marca: async () => marcaDesdeApi(await http.get<MarcaApi>('/admin/marca')),
    guardarMarca: async (m: Marca) => marcaDesdeApi(await http.put<MarcaApi>('/admin/marca', marcaHaciaApi(m))),
    parametros: async () => normalizarPagina(await http.get<Pagina<Parametro> | Parametro[]>('/admin/parametros')).items,
    /** PATCH /admin/parametros { valores: { clave: valor } } */
    guardarParametro: (clave: string, valor: unknown) => http.patch<unknown>('/admin/parametros', { valores: { [clave]: valor } }),
    onboarding: async () => normalizarOnboarding(await http.get<OnboardingApi>('/admin/onboarding')),
  },

  maestros: {
    listar: <T>(recurso: RecursoMaestro, query?: Query) => pagina<T>(`/${recurso}`, query),
    obtener: async <T>(recurso: RecursoMaestro, id: string) => {
      const r = await http.solicitar<T>('GET', `/${recurso}/${id}`);
      return { data: r.data, etag: r.etag };
    },
    crear: <T>(recurso: RecursoMaestro, body: unknown) => http.post<T>(`/${recurso}`, body, { idempotente: true }),
    actualizar: <T>(recurso: RecursoMaestro, id: string, body: unknown, etag: string | null) => http.patch<T>(`/${recurso}/${id}`, body, { ifMatch: etag }),
    cerrarVigencia: (recurso: RecursoMaestro, id: string, hasta: string, motivo: string) => http.post<void>(`/${recurso}/${id}/cerrar-vigencia`, { hasta, motivo }),
  },

  conflictos: {
    listar: () => pagina<ConflictoPersona>('/personas/conflictos'),
    /** adoptar = el registro pasa a la fuente; separar = error de RUT en la fuente, se mantiene el propio. */
    resolver: (personaId: string, decision: 'adoptar' | 'separar', comentario: string) =>
      http.post<void>(`/personas/conflictos/${personaId}/resolver`, { decision, comentario }),
  },

  cargas: {
    plantilla: (objeto: string) => http.descargar(`/cargas/plantillas/${objeto}`, { formato: 'xlsx' }, `plantilla-${objeto}.xlsx`),
    /** El API recibe el archivo en JSON (base64 para XLSX, texto para CSV) y lo valida completo sin aplicar. */
    subir: async (objeto: string, archivo: File) => {
      const esCsv = /\.csv$/i.test(archivo.name) || archivo.type === 'text/csv';
      const cuerpo: Record<string, unknown> = { objeto, formato: esCsv ? 'csv' : 'xlsx', nombre_archivo: archivo.name };
      if (esCsv) cuerpo.contenido_texto = await leerTexto(archivo);
      else cuerpo.contenido_base64 = await leerBase64(archivo);
      return http.post<Carga>('/cargas', cuerpo, { idempotente: true });
    },
    obtener: (id: string) => http.get<Carga>(`/cargas/${id}`),
    validar: (id: string) => http.post<Carga>(`/cargas/${id}/validar`),
    aplicar: (id: string) => http.post<Carga>(`/cargas/${id}/aplicar`),
  },

  tarifarios: {
    enviarAprobacion: (id: string) => http.post<Tarifario>(`/tarifarios/${id}/enviar-aprobacion`),
    aprobar: (id: string) => http.post<Tarifario>(`/tarifarios/${id}/aprobar`, {}),
    rechazar: (id: string, comentario: string) => http.post<Tarifario>(`/tarifarios/${id}/rechazar`, { comentario }),
    simular: async (id: string, datos: { desde: string; hasta: string }) => normalizarSimulacionTarifario(await http.post<SimulacionTarifarioApi>(`/tarifarios/${id}/simular`, datos)),
  },
  bonos: {
    /** Puerta 3: con alcance retroactivo el API exige confirmar el impacto legal mostrado en la simulación. */
    autorizar: (id: string, confirmo_impacto: boolean, comentario?: string) =>
      http.post<void>(`/bonos/${id}/autorizar`, { confirmo_impacto, ...(comentario ? { comentario } : {}) }),
    publicar: (id: string, autorizacion_superior = false) => http.post<void>(`/bonos/${id}/publicar`, { autorizacion_superior }),
    simular: async (id: string) => normalizarSimulacionBono(await http.post<SimulacionBonoApi>(`/bonos/${id}/simular`, {})),
  },
  metas: {
    enviarAprobacion: (id: string) => http.post<void>(`/metas/${id}/enviar-aprobacion`, {}),
    aprobar: (id: string) => http.post<void>(`/metas/${id}/aprobar`, {}),
  },

  jornadas: {
    listar: (query?: Query) => pagina<Jornada>('/jornadas', query),
    obtener: (id: string) => http.get<JornadaDetalle>(`/jornadas/${id}`),
    validar: (id: string) => http.post<Jornada>(`/jornadas/${id}/validar`),
    reabrir: (id: string, motivo: string) => http.post<Jornada>(`/jornadas/${id}/reabrir`, { motivo }),
  },

  eventos: {
    listar: (query?: Query) => pagina<Evento>('/eventos', query),
    /** Evento manual web: el API espera { evento: ItemEvento } (mismo camino de ingesta que la app). */
    crear: (evento: Record<string, unknown>) => http.post<Evento>('/eventos', { evento }, { idempotente: true }),
    /** Registro de tarja: `jornada` solo viaja en el primer evento de una jornada nueva. */
    registrar: (evento: Record<string, unknown>, jornada?: Record<string, unknown>) =>
      http.post<unknown>('/eventos', { ...(jornada ? { jornada } : {}), evento }, { idempotente: true }),
    anular: (id: string, motivo: string) => http.post<Evento>(`/eventos/${id}/anular`, { motivo }),
  },

  cuarentena: {
    listar: (query?: Query) => pagina<Cuarentena>('/cuarentena', query),
    resolver: (eventoId: string, resolucion: 'aceptado' | 'corregido' | 'rechazado', comentario: string) =>
      http.post<{ estado: string }>(`/cuarentena/${eventoId}/resolver`, { resolucion, comentario }),
  },

  repartos: {
    /** El API expone solo los pendientes (GET /repartos/pendientes). */
    pendientes: async () => (await pagina<RepartoApi>('/repartos/pendientes')).items.map(normalizarReparto),
    aprobar: (eventoId: string) => http.post<void>(`/eventos/${eventoId}/reparto/aprobar`, {}),
  },

  reclamos: {
    listar: (query?: Query) => pagina<Reclamo>('/reclamos', query),
    crear: (body: Record<string, unknown>) => http.post<Reclamo>('/reclamos', body, { idempotente: true }),
    clasificar: (id: string, clasificacion: Reclamo['clasificacion']) => http.post<Reclamo>(`/reclamos/${id}/clasificar`, { clasificacion }),
    asignar: (id: string, usuario_id: string, sla_fecha?: string) => http.post<Reclamo>(`/reclamos/${id}/asignar`, { usuario_id, ...(sla_fecha ? { sla_fecha } : {}) }),
    resolver: (id: string, body: { resolucion: string; rechazado: boolean }) => http.post<Reclamo>(`/reclamos/${id}/resolver`, { ...body, genera_ajuste: false }),
    aprobarAjuste: (id: string) => http.post<Reclamo>(`/reclamos/${id}/aprobar-ajuste`, {}),
  },

  periodos: {
    listar: (query?: Query) => pagina<Periodo>('/periodos', query),
    obtener: async (id: string) => normalizarPeriodo(await http.get<PeriodoApi>(`/periodos/${id}`)),
    crear: (sociedad_id: string, periodo: string) => http.post<Periodo>('/periodos', { sociedad_id, periodo }, { idempotente: true }),
    calcular: (id: string) => http.post<Periodo>(`/periodos/${id}/calcular`, {}),
    enviarRevision: (id: string) => http.post<Periodo>(`/periodos/${id}/enviar-revision`, {}),
    controles: async (id: string) => {
      const r = await http.get<Control[] | { items: Control[] }>(`/periodos/${id}/controles`);
      return Array.isArray(r) ? r : (r?.items ?? []);
    },
    aprobaciones: async (id: string) => normalizarPagina(await http.get<Pagina<Aprobacion> | Aprobacion[]>(`/periodos/${id}/aprobaciones`)).items,
    aprobarPuerta: (id: string, puerta: number, body: { comentario?: string; sociedad_contratista_id?: string }) =>
      http.post<Periodo>(`/periodos/${id}/puertas/${puerta}/aprobar`, body),
    rechazarPuerta: (id: string, puerta: number, body: { comentario: string; sociedad_contratista_id?: string }) =>
      http.post<Periodo>(`/periodos/${id}/puertas/${puerta}/rechazar`, body),
    congelar: (id: string) => http.post<{ hash_resultados: string; movimientos: number }>(`/periodos/${id}/congelar`, {}),
    /** Se invoca sobre el periodo CERRADO de origen; las diferencias se imputan al periodo abierto de destino. */
    ajustesRetroactivos: (periodoCerradoId: string, periodo_destino_id: string) =>
      http.post<{ movimientos: number }>(`/periodos/${periodoCerradoId}/ajustes-retroactivos`, { periodo_destino_id }),
  },

  resultados: {
    listar: (query: Query) => pagina<Resultado>('/resultados', query),
  },

  nomina: {
    lotes: (query?: Query) => pagina<LoteNomina>('/nomina/lotes', query),
    generar: (periodo_id: string, destino: string) => http.post<LoteNomina>('/nomina/lotes', { periodo_id, destino }, { idempotente: true }),
    contenido: (id: string) => http.get<{ lineas: { movimiento_id: string; rut: string; concepto: string; importe: number; id_en_nomina: string }[] }>(`/nomina/lotes/${id}/contenido`, { formato: 'json' }),
    descargar: (id: string, formato: 'json' | 'csv') => http.descargar(`/nomina/lotes/${id}/contenido`, { formato }, `lote-${id}.${formato}`),
    registrarAcuses: (id: string, acuses: AcuseLinea[]) => http.post<LoteNomina>(`/nomina/lotes/${id}/acuses`, { acuses }),
  },

  reportes: {
    catalogo: async () => (await pagina<{ codigo: string; nombre: string; parametros?: string[]; formatos?: string[] }>('/reportes')).items,
    descargar: (codigo: string, formato: 'xlsx' | 'pdf', query: Query) => http.descargar(`/reportes/${codigo}`, { ...query, formato }, `${codigo}.${formato}`),
  },

  auditoria: {
    listar: (query?: Query) => pagina<Auditoria>('/auditoria', query),
    exportarCsv: (query?: Query) => http.descargar('/auditoria', { ...query, formato: 'csv' }, 'auditoria.csv'),
  },

  integraciones: {
    listar: () => pagina<Integracion>('/integraciones'),
    crear: (body: Partial<Integracion>) => http.post<Integracion>('/integraciones', body, { idempotente: true }),
    actualizar: (id: string, body: Partial<Integracion>) => http.patch<Integracion>(`/integraciones/${id}`, body),
    sincronizar: (id: string, modo: 'delta' | 'full' = 'delta') => http.post<void>(`/integraciones/${id}/sincronizar`, { modo }),
    logs: (id: string) => pagina<LogIntegracion>(`/integraciones/${id}/logs`),
    generarApiKey: (id: string, ips_permitidas: string[]) => http.post<{ api_key: string }>(`/integraciones/${id}/api-key`, { ips_permitidas }),
  },

  dispositivos: {
    listar: (query?: Query) => pagina<Dispositivo>('/devices', query),
    codigoEnrolamiento: (sociedad_id: string) => http.post<CodigoEnrolamiento>('/devices/enrollment-codes', { sociedad_id }),
    bloquear: (id: string) => http.post<void>(`/devices/${id}/block`, {}),
  },

  usuarios: {
    listar: (query?: Query) => pagina<UsuarioAdmin>('/usuarios', query),
    crear: (body: { login: string; email: string; nombre: string; tipo: 'interno' | 'externo'; rol_base: string; permisos: PermisoAsignacion[] }) =>
      http.post<RespuestaInvitacion>('/usuarios', body, { idempotente: true }),
    cambiar: (id: string, body: { rol_base?: string; activo?: boolean; nombre?: string; mfa_obligatorio?: boolean }) => http.patch<unknown>(`/usuarios/${id}`, body),
    asignarPermisos: (id: string, permisos: PermisoAsignacion[]) => http.put<RespuestaPermisos>(`/usuarios/${id}/permisos`, { permisos }),
    reinvitar: (id: string) => http.post<{ enlace_invitacion: string; expira_en_s: number }>(`/usuarios/${id}/invitacion`, {}),
    /** Enlace de un uso para definir (invitación) o restablecer la contraseña de un miembro. */
    enlaceAcceso: (id: string) => http.post<EnlaceAcceso>(`/usuarios/${id}/acceso`, {}),
  },

  /** Portal de administración global (solo administradores de plataforma; no requiere organización activa). */
  plataforma: {
    resumen: () => http.get<ResumenPlataforma>('/plataforma/resumen'),
    organizaciones: (query?: { q?: string; estado?: string }) => http.get<Pagina<OrganizacionResumen>>('/plataforma/organizaciones', query),
    organizacion: (id: string) => http.get<OrganizacionDetalle>(`/plataforma/organizaciones/${id}`),
    crearOrganizacion: (body: { nombre: string; rut: string; administrador?: { nombre: string; email: string } }) =>
      http.post<RespuestaOrganizacion>('/plataforma/organizaciones', body, { idempotente: true }),
    cambiarOrganizacion: (id: string, body: { nombre?: string; estado?: 'activo' | 'suspendido' }) => http.patch<unknown>(`/plataforma/organizaciones/${id}`, body),
    /** Gestión de miembros de una organización, con la misma forma que `api.usuarios` (lo usa GestionMiembros). */
    miembros: (tenantId: string) => ({
      crear: (body: Parameters<typeof api.usuarios.crear>[0]) => http.post<RespuestaInvitacion>(`/plataforma/organizaciones/${tenantId}/usuarios`, body, { idempotente: true }),
      cambiar: (id: string, body: Parameters<typeof api.usuarios.cambiar>[1]) => http.patch<unknown>(`/plataforma/organizaciones/${tenantId}/usuarios/${id}`, body),
      asignarPermisos: (id: string, permisos: PermisoAsignacion[]) => http.put<RespuestaPermisos>(`/plataforma/organizaciones/${tenantId}/usuarios/${id}/permisos`, { permisos }),
      reinvitar: (id: string) => http.post<{ enlace_invitacion: string; expira_en_s: number }>(`/plataforma/organizaciones/${tenantId}/usuarios/${id}/invitacion`, {}),
      enlaceAcceso: (id: string) => http.post<EnlaceAcceso>(`/plataforma/usuarios/${id}/acceso`, {}),
    }),
    usuarios: (query?: { q?: string; tenant_id?: string; admin_plataforma?: 'true' | 'false' }) => http.get<Pagina<UsuarioPlataforma>>('/plataforma/usuarios', query),
    crearAdmin: (body: { email: string; nombre: string }) => http.post<{ usuario_id: string } & EnlaceAcceso>('/plataforma/usuarios', body, { idempotente: true }),
    cambiarUsuario: (id: string, body: { nombre?: string; estado?: 'activo' | 'bloqueado'; admin_plataforma?: boolean; reiniciar_mfa?: true }) =>
      http.patch<unknown>(`/plataforma/usuarios/${id}`, body),
    enlaceAcceso: (id: string) => http.post<EnlaceAcceso>(`/plataforma/usuarios/${id}/acceso`, {}),
    auditoria: (query?: { tenant_id?: string; usuario_id?: string; limite?: number }) => http.get<Pagina<RegistroAuditoriaPlataforma>>('/plataforma/auditoria', query),
  },

  trabajadores: {
    /** Envía al trabajador un enlace de un uso (15 min) a su comprobante o al acuse del tarifario. */
    enviarEnlace: (personaId: string, proposito: 'comprobante' | 'acuse_tarifario' = 'comprobante') =>
      http.post<{ enviado: boolean; canal: string; sin_telefono?: boolean; expira_en_s?: number }>(`/trabajadores/${personaId}/enlace`, { proposito }),
  },
};

export type Api = typeof api;


// ------------------------------------------------------------------------------------------------
// Normalizaciones de respuestas de apps/api a los tipos de la interfaz
// ------------------------------------------------------------------------------------------------

function leerTexto(f: File): Promise<string> {
  return typeof f.text === 'function' ? f.text() : new Response(f).text();
}

async function leerBase64(f: File): Promise<string> {
  const buf = new Uint8Array(await new Response(f).arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(bin);
}

export interface RepartoApi {
  evento_id: string;
  sociedad_id?: string;
  cuadrilla_id?: string | null;
  labor_codigo: string;
  fecha: string;
  cantidad: number | string;
  integrantes?: { persona_id: string; porcentaje: number | string; nombre?: string }[];
  lineas?: Reparto['lineas'];
  metodo?: Reparto['metodo'];
  cuadrilla_nombre?: string;
  unidad?: string | null;
}

export function normalizarReparto(r: RepartoApi): Reparto {
  const lineas = r.lineas ?? (r.integrantes ?? []).map((i) => ({ persona_id: i.persona_id, nombre: i.nombre ?? i.persona_id.slice(0, 8), porcentaje: i.porcentaje }));
  const out: Reparto = { evento_id: r.evento_id, fecha: r.fecha, labor_codigo: r.labor_codigo, cantidad: r.cantidad, metodo: r.metodo ?? 'ponderador', estado: 'pendiente', lineas };
  const cuadrilla = r.cuadrilla_nombre ?? (r.cuadrilla_id ? r.cuadrilla_id.slice(0, 8) : undefined);
  if (cuadrilla) out.cuadrilla_nombre = cuadrilla;
  if (r.unidad !== undefined) out.unidad = r.unidad;
  return out;
}

export type PeriodoApi = Omit<Periodo, 'contratistas'> & { contratistas?: (string | { sociedad_id: string; nombre: string; aprobado: boolean })[] };

/** El API devuelve `contratistas` como ids; el estado de aprobación se deduce de las aprobaciones de la puerta 5. */
export function normalizarPeriodo(p: PeriodoApi): Periodo {
  const aprobaciones = p.aprobaciones ?? [];
  const ultimoRechazo = aprobaciones.filter((a) => a.estado === 'rechazado').map((a) => a.aprobado_en ?? '').sort().pop() ?? '';
  const contratistas = (p.contratistas ?? []).map((c) =>
    typeof c === 'string'
      ? {
          sociedad_id: c,
          nombre: c,
          aprobado: aprobaciones.some((a) => a.puerta === 5 && a.estado === 'aprobado' && a.sociedad_contratista_id === c && (a.aprobado_en ?? '') > ultimoRechazo),
        }
      : c,
  );
  return { ...p, contratistas };
}

export interface SimulacionTarifarioApi {
  costo_actual?: number;
  costo_simulado?: number;
  diferencia?: number;
  creditos?: number;
  sin_tarifa?: number;
  por_cuartel?: { cuartel: string; actual: number; simulado: number; diferencia: number }[];
  por_trabajador?: unknown[];
  // forma de la interfaz (modo simulado)
  costo_total?: number;
  costo_referencia?: number | null;
  detalle?: ResultadoSimulacion['detalle'];
  personas?: number;
  advertencias?: string[];
}

export function normalizarSimulacionTarifario(r: SimulacionTarifarioApi): ResultadoSimulacion {
  if (r.costo_total !== undefined) return r as ResultadoSimulacion;
  const out: ResultadoSimulacion = {
    costo_total: r.costo_simulado ?? 0,
    costo_referencia: r.costo_actual ?? null,
    diferencia: r.diferencia ?? (r.costo_simulado ?? 0) - (r.costo_actual ?? 0),
    detalle: (r.por_cuartel ?? []).map((c) => ({ clave: c.cuartel, etiqueta: c.cuartel, costo: c.simulado, costo_referencia: c.actual })),
    advertencias: r.sin_tarifa ? [`${r.sin_tarifa} crédito(s) sin tarifa en esta versión.`] : [],
  };
  if (Array.isArray(r.por_trabajador)) out.personas = r.por_trabajador.length;
  return out;
}

type AlcanceBono = { costo: number; trabajadores: number; dias: number; sobre_presupuesto: boolean };
export interface SimulacionBonoApi {
  alcance_actual?: 'desde_publicacion' | 'todo_el_periodo_abierto';
  presupuesto_tope?: number | null;
  desde_publicacion?: AlcanceBono;
  todo_el_periodo_abierto?: AlcanceBono;
  impacto_legal?: { concepto?: string; devengo_diario?: boolean | null; incide_en?: string[]; principalidad?: string | null; ordinariedad?: string | null };
  costo_total?: number;
}

export function normalizarSimulacionBono(r: SimulacionBonoApi): ResultadoSimulacion {
  if (r.costo_total !== undefined) return r as ResultadoSimulacion;
  const actual = r.alcance_actual === 'todo_el_periodo_abierto' ? r.todo_el_periodo_abierto : r.desde_publicacion;
  const adv: string[] = [];
  if (actual?.sobre_presupuesto) adv.push('El costo proyectado supera el presupuesto tope.');
  const il = r.impacto_legal;
  if (il) adv.push(`Impacto legal: concepto ${il.concepto ?? '—'}; incide en ${(il.incide_en ?? []).join(', ') || '—'}; devengo diario ${il.devengo_diario ? 'sí' : 'no'}.`);
  const detalle: NonNullable<ResultadoSimulacion['detalle']> = [];
  if (r.desde_publicacion) detalle.push({ clave: 'desde_publicacion', etiqueta: 'Alcance: desde la publicación', costo: r.desde_publicacion.costo, cantidad: r.desde_publicacion.trabajadores });
  if (r.todo_el_periodo_abierto) detalle.push({ clave: 'todo_el_periodo_abierto', etiqueta: 'Alcance: todo el periodo abierto', costo: r.todo_el_periodo_abierto.costo, cantidad: r.todo_el_periodo_abierto.trabajadores });
  const out: ResultadoSimulacion = { costo_total: actual?.costo ?? 0, detalle, advertencias: adv };
  if (actual) out.personas = actual.trabajadores;
  return out;
}

/** Marca en apps/api (nombre_comercial, color_texto_sobre_primario, dominio…). */
export interface MarcaApi {
  nombre_comercial?: string;
  color_primario?: string;
  color_fondo?: string;
  color_texto?: string;
  color_texto_sobre_primario?: string;
  tipografia?: string | null;
  logo_url?: string | null;
  dominio?: string | null;
  pie_legal?: string | null;
  terminos_url?: string | null;
  privacidad_url?: string | null;
  // forma de la interfaz (modo simulado)
  nombre_producto?: string;
  color_texto_primario?: string;
  color_superficie?: string;
  color_acento?: string;
}

export function marcaDesdeApi(m: MarcaApi): Marca & { dominio?: string | null } {
  const out: Marca & { dominio?: string | null } = {
    color_primario: m.color_primario ?? '',
    color_texto_primario: m.color_texto_sobre_primario ?? m.color_texto_primario ?? '',
    color_fondo: m.color_fondo ?? '',
    color_texto: m.color_texto ?? '',
  };
  const nombre = m.nombre_comercial ?? m.nombre_producto;
  if (nombre) out.nombre_producto = nombre;
  if (m.color_superficie) out.color_superficie = m.color_superficie;
  if (m.color_acento) out.color_acento = m.color_acento;
  if (m.logo_url !== undefined) out.logo_url = m.logo_url;
  if (m.pie_legal !== undefined) out.pie_legal = m.pie_legal;
  if (m.tipografia !== undefined) out.tipografia = m.tipografia;
  if (m.dominio !== undefined) out.dominio = m.dominio;
  // Colores vacíos no se aplican (se mantiene la marca por defecto)
  for (const k of ['color_primario', 'color_texto_primario', 'color_fondo', 'color_texto'] as const) if (!out[k]) delete (out as Partial<Marca>)[k];
  return out;
}

export function marcaHaciaApi(m: Marca & { dominio?: string | null }): MarcaApi {
  const out: MarcaApi = {
    nombre_comercial: m.nombre_producto ?? 'Tarja',
    color_primario: m.color_primario,
    color_fondo: m.color_fondo,
    color_texto: m.color_texto,
    color_texto_sobre_primario: m.color_texto_primario,
    logo_url: m.logo_url || null,
    pie_legal: m.pie_legal || null,
  };
  if (m.tipografia) out.tipografia = m.tipografia;
  if (m.dominio !== undefined) out.dominio = m.dominio || null;
  return out;
}

export interface OnboardingApi {
  completo: boolean;
  visible?: boolean;
  pasos: ({ paso?: number; nombre?: string; completo: boolean; chequeos?: { codigo: string; descripcion: string; ok: boolean; detalle?: unknown }[] } & Partial<Onboarding['pasos'][number]>)[];
}

export function normalizarOnboarding(o: OnboardingApi): Onboarding {
  return {
    completo: o.completo,
    pasos: o.pasos.map((p, i) => {
      const pendientes = (p.chequeos ?? []).filter((c) => !c.ok).map((c) => c.descripcion);
      const r: Onboarding['pasos'][number] = { codigo: p.codigo ?? String(p.paso ?? i + 1), titulo: p.titulo ?? p.nombre ?? `Paso ${i + 1}`, completo: p.completo };
      const desc = p.descripcion ?? (p.chequeos ?? []).map((c) => c.descripcion).join(' · ');
      if (desc) r.descripcion = desc;
      const det = p.detalle ?? (pendientes.length ? pendientes.join('; ') : null);
      if (det) r.detalle = det;
      if (p.enlace) r.enlace = p.enlace;
      if (p.chequeos) r.chequeos = p.chequeos.map((c) => ({ codigo: c.codigo, descripcion: c.descripcion, ok: c.ok }));
      return r;
    }),
  };
}
